import React, { useState } from 'react';
import { Autocomplete, TextField, CircularProgress, Chip } from '@mui/material';
import { NotificationChannel } from '../models';
import { useGetNotificationChannelsQuery } from '../services/connectApi';

interface ConnectChannelAutocompleteProps {
  value: NotificationChannel[] | null;
  onChange: (value: NotificationChannel[]) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
}

const ConnectChannelAutocomplete: React.FC<ConnectChannelAutocompleteProps> = ({ 
  value, 
  onChange, 
  label = 'Channels', 
  placeholder = 'Search channels by name or type',
  error,
  helperText
}) => {
  const [inputValue, setInputValue] = useState('');

  // Get channels from the API
  const { data, isLoading } = useGetNotificationChannelsQuery({ 
    page: 1, 
    page_size: 50
  });
  
  const channels = data?.results || [];
  
  // Filter active channels only
  const activeChannels = channels.filter(channel => channel.is_active);

  return (
    <Autocomplete
      multiple
      options={activeChannels}
      value={value || []}
      onChange={(_e, newValue) => onChange(newValue)}
      getOptionLabel={(option) => `${option.name} (${getChannelTypeLabel(option.type)})`}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterSelectedOptions
      loading={isLoading}
      inputValue={inputValue}
      onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            label={`${option.name} (${getChannelTypeLabel(option.type)})`}
            {...getTagProps({ index })}
            key={option.id}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

// Helper function to get a display-friendly channel type label
const getChannelTypeLabel = (type: string): string => {
  switch (type) {
    case 'push':
      return 'Push Notification';
    case 'email':
      return 'Email';
    case 'sms':
      return 'SMS';
    case 'whatsapp':
      return 'WhatsApp';
    default:
      return type;
  }
};

export default ConnectChannelAutocomplete;
